import React from 'react'
import { Table, Badge, Spinner } from 'react-bootstrap'
import moment from 'moment'
import { PermissionEvent } from 'hooks/use-permission-events'

interface PermissionEventsTableProps {
  events: PermissionEvent[]
  loading?: boolean
  onLoadMore?: () => void
  hasMore?: boolean
  onEventClick?: (systemUser: string, recipient: string) => void
}

const PermissionEventsTable: React.FC<PermissionEventsTableProps> = ({
  events,
  loading,
  onLoadMore,
  hasMore,
  onEventClick,
}) => {
  const formatTimestamp = (timestamp: string) => {
    return moment(timestamp).format('MMM DD, YYYY HH:mm:ss')
  }

  const getDecisionBadge = (finalDecision: boolean) => {
    return (
      <Badge variant={finalDecision ? 'success' : 'danger'} className="fs-6">
        {finalDecision ? 'ALLOWED' : 'BLOCKED'}
      </Badge>
    )
  }

  const getPanopticonBadge = (canSend: boolean, reason: string) => {
    return (
      <div className="d-flex flex-column text-center">
        <Badge variant={canSend ? 'primary' : 'warning'} className="mb-1">
          {canSend ? 'ALLOW' : 'BLOCK'}
        </Badge>
        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
          {reason}
        </small>
      </div>
    )
  }

  const getResponseRateBadge = (rate: number) => {
    let variant = 'secondary'
    if (rate >= 70) variant = 'success'
    else if (rate >= 30) variant = 'warning'
    else variant = 'danger'

    return (
      <Badge variant={variant} className="fs-6">
        {rate}%
      </Badge>
    )
  }

  if (loading && events.length === 0) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <div className="mt-2">Loading permission events...</div>
      </div>
    )
  }

  if (events.length === 0) {
    return (
      <div className="text-center p-4">
        <div className="text-muted">No permission events found</div>
      </div>
    )
  }

  return (
    <div>
      <Table striped bordered hover responsive className="mb-0 text-center">
        <thead className="table-dark">
          <tr>
            <th style={{ minWidth: '150px' }} className="text-center">
              Timestamp
            </th>
            <th style={{ minWidth: '130px' }} className="text-center">
              Recipient
            </th>
            <th style={{ minWidth: '100px' }} className="text-center">
              Final Decision
            </th>
            <th style={{ minWidth: '100px' }} className="text-center">
              Panopticon
            </th>
            <th style={{ minWidth: '80px' }} className="text-center">
              Response Rate
            </th>
            <th style={{ minWidth: '60px' }} className="text-center">
              Unreplied
            </th>
            <th style={{ minWidth: '80px' }} className="text-center">
              Replied Today
            </th>
            <th style={{ minWidth: '80px' }} className="text-center">
              Ever Replied
            </th>
            <th style={{ minWidth: '100px' }} className="text-center">
              User/Chat ID
            </th>
          </tr>
        </thead>
        <tbody>
          {events.map((event, index) => (
            <tr
              key={`${event.SK}-${index}`}
              onClick={() =>
                onEventClick?.(event.senderNumber, event.recipientNumber)
              }
              style={{
                cursor: onEventClick ? 'pointer' : 'default',
                transition: 'background-color 0.2s ease',
              }}
              className={onEventClick ? 'table-row-hover' : ''}
            >
              <td>
                <small>{formatTimestamp(event.timestamp)}</small>
              </td>
              <td>
                <code style={{ fontSize: '0.8rem' }}>
                  {event.recipientNumber}
                </code>
              </td>
              <td>{getDecisionBadge(event.finalDecision)}</td>
              <td>
                {getPanopticonBadge(event.panopticonCanSend, event.reason)}
              </td>
              <td>{getResponseRateBadge(event.responseRate)}</td>
              <td>
                <Badge
                  variant={event.unrepliedCount > 5 ? 'warning' : 'secondary'}
                >
                  {event.unrepliedCount}
                </Badge>
              </td>
              <td>
                <Badge
                  variant={event.hasRepliedToday ? 'success' : 'secondary'}
                >
                  {event.hasRepliedToday ? 'Yes' : 'No'}
                </Badge>
              </td>
              <td>
                <Badge variant={event.hasEverReplied ? 'success' : 'danger'}>
                  {event.hasEverReplied ? 'Yes' : 'No'}
                </Badge>
              </td>
              <td>
                <div style={{ fontSize: '0.75rem' }}>
                  <div>User: {event.userId}</div>
                  <div className="text-muted">Chat: {event.chatId}</div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Load More Section */}
      {hasMore && (
        <div className="text-center p-3 border-top">
          {loading ? (
            <div>
              <Spinner animation="border" size="sm" className="me-2" />
              Loading more events...
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={onLoadMore}
              disabled={loading}
            >
              Load More Events
            </button>
          )}
        </div>
      )}

      {!hasMore && events.length > 0 && (
        <div className="text-center p-3 border-top text-muted">
          <small>All events loaded ({events.length} total)</small>
        </div>
      )}
    </div>
  )
}

export default PermissionEventsTable
